import { Phone, MessageCircle, Mail, CheckCircle2, ShieldCheck, Star, Clock, MapPin, ArrowRight, Radio } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const TRUST_BADGES = [{
  Icon: ShieldCheck,
  label: "1-Year Labour Warranty"
}, {
  Icon: Star,
  label: "5.0 · 200+ Reviews"
}, {
  Icon: Clock,
  label: "Same-day in most suburbs"
}, {
  Icon: MapPin,
  label: "217+ Sydney suburbs"
}];
export default function RemoteCTA() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY_DEEP,
    fontFamily: FONT_BODY,
    paddingTop: "120px",
    paddingBottom: "120px"
  }}>
      {/* Ambient radial — more intense */}
      <div aria-hidden className="absolute inset-0" style={{
      background: `radial-gradient(ellipse at center, ${NAVY_LIGHT}88 0%, ${NAVY} 40%, ${NAVY_DEEP} 80%), radial-gradient(circle at 50% 100%, ${GOLD}22 0%, transparent 50%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD} 50%, transparent)`
    }} />

      {/* Background watermark — large */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
      fontFamily: FONT_HEAD,
      fontSize: "min(40vw, 600px)",
      fontWeight: 900,
      lineHeight: 0.9,
      letterSpacing: "-0.06em",
      color: `${GOLD}08`,
      whiteSpace: "nowrap"
    }}>
        10
      </div>

      {/* Decorative dots top-left */}
      <div aria-hidden className="absolute top-20 left-10 w-48 h-48 pointer-events-none" style={{
      backgroundImage: `radial-gradient(circle, ${GOLD}44 1.5px, transparent 2px)`,
      backgroundSize: "14px 14px",
      opacity: 0.6,
      maskImage: "radial-gradient(circle at top left, black 0%, transparent 70%)"
    }} />
      {/* Decorative dots bottom-right */}
      <div aria-hidden className="absolute bottom-20 right-10 w-48 h-48 pointer-events-none" style={{
      backgroundImage: `radial-gradient(circle, ${GOLD}44 1.5px, transparent 2px)`,
      backgroundSize: "14px 14px",
      opacity: 0.6,
      maskImage: "radial-gradient(circle at bottom right, black 0%, transparent 70%)"
    }} />

      <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10 text-center">
        {/* Eyebrow */}
        <div className="mb-7 flex items-center justify-center gap-3">
          <div aria-hidden className="h-px w-10" style={{
          backgroundColor: GOLD
        }} />
          <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
            <Radio size={14} color={GOLD} strokeWidth={2.5} />
          </span>
          <span style={{
          fontFamily: FONT_HEAD,
          fontSize: "12px",
          fontWeight: 700,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: GOLD
        }}>
            10 · Let's get you clicking
          </span>
          <div aria-hidden className="h-px w-10" style={{
          backgroundColor: GOLD
        }} />
        </div>

        {/* Massive H2 */}
        <h2 style={{
        fontFamily: FONT_HEAD,
        fontWeight: 800,
        fontSize: "clamp(2.6rem, 6vw, 5rem)",
        lineHeight: 1.02,
        letterSpacing: "-0.03em",
        color: "#ffffff",
        marginBottom: "24px",
        maxWidth: "900px",
        marginLeft: "auto",
        marginRight: "auto"
      }}>
          One call.
          <br />
          <span style={{
          color: GOLD
        }}>
            One synced remote.
          </span>
          <br />
          Door open again — today.
        </h2>

        <p style={{
        fontFamily: FONT_BODY,
        fontSize: "clamp(1rem, 1.3vw, 1.2rem)",
        lineHeight: 1.65,
        color: "rgba(255,255,255,0.72)",
        maxWidth: "640px",
        marginLeft: "auto",
        marginRight: "auto",
        marginBottom: "44px"
      }}>
          Tell us your brand. We'll bring the right gear, pair it on-site, and
          show you how it all works. Flat price quoted on the phone — no
          surprises, no hourly games.
        </p>

        {/* Phone number — visual element */}
        <a href="tel:0485000933" className="inline-block mb-10 transition-all hover:scale-[1.02]" style={{
        textDecoration: "none"
      }}>
          <div style={{
          fontFamily: FONT_HEAD,
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.25em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.5)",
          marginBottom: "6px"
        }}>
            Call us direct
          </div>
          <div style={{
          fontFamily: FONT_HEAD,
          fontSize: "clamp(2.4rem, 5vw, 3.6rem)",
          fontWeight: 900,
          lineHeight: 1,
          color: GOLD,
          letterSpacing: "-0.025em",
          textShadow: `0 0 60px ${GOLD}44`
        }}>
            0468&nbsp;688&nbsp;145
          </div>
        </a>

        {/* CTA cluster */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          <a href="tel:0485000933" className="inline-flex items-center gap-2.5 px-7 py-4 rounded-full transition-all hover:scale-[1.02] hover:shadow-2xl" style={{
          backgroundColor: GOLD,
          color: NAVY,
          fontFamily: FONT_HEAD,
          fontWeight: 700,
          fontSize: "15px",
          boxShadow: `0 12px 36px ${GOLD}44`
        }}>
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <Phone size={17} strokeWidth={2.5} />
            </span>
            Call now
          </a>

          <a href="https://wa.me/61485000933?text=Hi%2C%20I%20need%20a%20garage%20remote%20%2F%20keypad%20programmed.%20Can%20you%20help%3F" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 px-7 py-4 rounded-full transition-all hover:scale-[1.02]" style={{
          backgroundColor: "#25D366",
          color: "#081438",
          fontFamily: FONT_HEAD,
          fontWeight: 700,
          fontSize: "15px"
        }}>
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <MessageCircle size={17} strokeWidth={2.5} />
            </span>
            WhatsApp
          </a>

          <Link href="#contact-us" className="inline-flex items-center gap-2.5 px-7 py-4 rounded-full border-2 transition-all hover:bg-white/5" style={{
          borderColor: "rgba(255,255,255,0.25)",
          color: "#ffffff",
          fontFamily: FONT_HEAD,
          fontWeight: 700,
          fontSize: "15px",
          backgroundColor: "transparent"
        }}>
            Free quote form
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <ArrowRight size={15} strokeWidth={2.5} />
            </span>
          </Link>
        </div>

        {/* Email line */}
        <a href="mailto:goldgaragedoor1@gmail.com" className="inline-flex items-center gap-2 mb-12 transition-colors hover:text-white" style={{
        fontFamily: FONT_BODY,
        fontSize: "14px",
        color: "rgba(255,255,255,0.55)",
        textDecoration: "none"
      }}>
          <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
            <Mail size={14} strokeWidth={2.5} />
          </span>
          goldgaragedoor1@gmail.com
        </a>

        {/* Trust badges */}
        <div className="pt-10 border-t max-w-3xl mx-auto" style={{
        borderColor: "rgba(255,255,255,0.1)"
      }}>
          <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-4">
            {TRUST_BADGES.map(({
            Icon,
            label
          }) => <div key={label} className="flex items-center gap-2">
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <Icon size={15} color={GOLD} strokeWidth={2.5} />
                </span>
                <span style={{
              fontFamily: FONT_BODY,
              fontSize: "13px",
              color: "rgba(255,255,255,0.78)",
              fontWeight: 600
            }}>
                  {label}
                </span>
              </div>)}
          </div>
        </div>

        {/* GG signature */}
        <div className="mt-12 flex items-center justify-center gap-3">
          <span style={{
          fontFamily: FONT_HEAD,
          fontSize: "14px",
          fontWeight: 800,
          letterSpacing: "0.22em",
          color: GOLD
        }}>
            GG
          </span>
          <span style={{
          fontFamily: FONT_BODY,
          fontSize: "12px",
          color: "rgba(255,255,255,0.4)",
          letterSpacing: "0.08em",
          textTransform: "uppercase"
        }}>
            Gold Garage Door · Bass Hill, NSW · Est. 2009
          </span>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD} 50%, transparent)`
    }} />
    </section>;
}